import { useState } from "react";
import { Link } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { originalList } from "@/mocks/originals";
import { videoList } from "@/mocks/videos";
import SectionHeading from "@/components/base/SectionHeading";
import Tag from "@/components/base/Tag";
import ContentCard, { formatViews } from "@/components/feature/ContentCard";
import VideoModal from "@/components/feature/VideoModal";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryEntry from "@/pages/home/components/CategoryEntry";
import NewsListItem from "@/pages/home/components/NewsListItem";
import VideoPreviewCard from "@/pages/home/components/VideoPreviewCard";

const hotTags = ["盛典", "定档", "新专辑", "综艺", "短剧", "音乐节", "创意手工", "树洞"];

export default function Home() {
  const [playItem, setPlayItem] = useState<{ title: string; videoUrl: string; cover: string } | null>(null);

  const latestNews = newsList.slice(0, 6);
  const featuredOriginals = originalList.slice(0, 3);
  const featuredVideos = videoList.slice(0, 3);
  const hotVideos = videoList.slice(0, 4);

  const openVideo = (item: { title: string; videoUrl: string; cover: string }) =>
    setPlayItem(item);

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
            我爱搞 · 一起搞点好玩的
          </h1>
          <p className="mt-1 text-xs text-foreground-500 md:text-sm">
            最新娱乐资讯 · 原创趣味内容 · 多元视频 · 互动社区，一站看个够
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/news"
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            <i className="ri-fire-line text-base"></i>
            看今日热点
          </Link>
        </div>
      </div>

      <HeroCarousel />

      <section className="mt-8">
        <SectionHeading title="快捷分类入口" subtitle="按兴趣直达你喜欢的板块" icon="ri-apps-2-line" />
        <CategoryEntry />
      </section>

      <section className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionHeading
            title="最新娱乐资讯"
            subtitle="按发布时间倒序更新"
            icon="ri-newspaper-line"
            moreTo="/news"
          />
          <div className="flex flex-col gap-3">
            {latestNews.map((item) => (
              <NewsListItem key={item.id} item={item} />
            ))}
          </div>
        </div>

        <aside className="flex flex-col gap-6">
          <div className="rounded-xl border border-background-200 bg-background-100 p-4 md:p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                <i className="ri-fire-line text-lg"></i>
              </span>
              热门视频
            </h2>
            <div className="mt-4 flex flex-col gap-3">
              {hotVideos.map((item) => (
                <div key={item.id} className="group flex gap-3">
                  <div className="relative h-14 w-24 shrink-0 overflow-hidden rounded-lg bg-background-200">
                    <Link to={`/detail/${item.id}`} aria-label={item.title}>
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 我爱搞`}
                        className="h-full w-full object-cover object-top"
                      />
                    </Link>
                    <button
                      type="button"
                      onClick={() => openVideo(item)}
                      aria-label={`播放 ${item.title}`}
                      className="absolute inset-0 flex cursor-pointer items-center justify-center bg-foreground-950/20 text-background-50 transition-colors hover:bg-foreground-950/35"
                    >
                      <i className="ri-play-fill text-xl"></i>
                    </button>
                  </div>
                  <div className="min-w-0">
                    <Link to={`/detail/${item.id}`}>
                      <h3 className="text-sm font-medium leading-snug text-foreground-800 transition-colors hover:text-primary-600 line-clamp-2">
                        {item.title}
                      </h3>
                    </Link>
                    <p className="mt-1 text-xs text-foreground-400">{formatViews(item.plays)} 次播放</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <span className="flex h-5 w-5 items-center justify-center text-accent-500">
                <i className="ri-price-tag-3-line text-lg"></i>
              </span>
              热门标签
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {hotTags.map((tag, index) => (
                <Link key={tag} to={`/search?q=${encodeURIComponent(tag)}`} className="cursor-pointer">
                  <Tag label={tag} variant={index % 2 === 0 ? "primary" : "secondary"} icon="ri-hashtag" />
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </section>

      <section className="mt-12">
        <SectionHeading
          title="趣味原创精选"
          subtitle="编辑精选的有趣原创内容"
          icon="ri-quill-pen-line"
          moreTo="/original"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
          {featuredOriginals.map((item) => (
            <ContentCard key={item.id} item={item} tagVariant="accent" />
          ))}
        </div>
      </section>

      <section className="mt-12">
        <SectionHeading
          title="热门视频推荐"
          subtitle="点击播放按钮即可预览"
          icon="ri-play-circle-line"
          moreTo="/video"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
          {featuredVideos.map((item) => (
            <VideoPreviewCard key={item.id} item={item} onPlay={openVideo} />
          ))}
        </div>
      </section>

      <section className="mt-14 overflow-hidden rounded-xl border border-background-200 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="flex flex-col justify-center p-6 md:p-10">
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              <i className="ri-sparkling-2-line text-sm"></i>
              关于我爱搞
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold leading-snug text-foreground-950 md:text-2xl">
              一个轻松、有趣、不端着的内容社区
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              我爱搞致力于为喜爱娱乐的你提供最新鲜的资讯、最好玩的原创内容与最多元的视频体验。我们相信，娱乐不只是消遣，更是一种分享快乐的方式。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
              >
                <i className="ri-information-line text-base"></i>
                了解我们
              </Link>
              <Link
                to="/community"
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50"
              >
                <i className="ri-chat-smile-2-line text-base"></i>
                去社区逛逛
              </Link>
            </div>
          </div>
          <div className="h-56 w-full lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=cheerful%20group%20of%20friends%20relaxing%20with%20popcorn%20and%20screens%20in%20a%20warm%20cozy%20living%20room%2C%20fun%20entertainment%20lifestyle%20mood%2C%20editorial%20photography%2C%20warm%20orange%20and%20cream%20tones%2C%20clean%20composition&width=900&height=700&seq=home-about-banner&orientation=landscape"
              alt="我爱搞内容社区"
              title="我爱搞内容社区 - 综合娱乐平台"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <VideoModal video={playItem} onClose={() => setPlayItem(null)} />
    </div>
  );
}